import PropTypes from 'prop-types';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as nyc from 'ui-library/NYC';
import ConfigureStageModal from 'workflows/configuration/ConfigureStageModal';
import WorkflowInputTable, {StageRunRow} from 'workflows/configuration/WorkflowInputTable';
import {InputType} from 'workflows/stage-entries/components/ExternalInputPicker';

import ConfigureStep from './components/ConfigureStep';
import StageConfigurationTable from './components/StageConfigurationTable';
import WorkflowConfigurationHeader from './components/WorkflowConfigurationHeader';
import {getWorkflowDisplayString} from './util/WorkflowUtil';
import * as WorkflowActions from './WorkflowActions';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  STAGE_FLOW_NODE_FULL_SHAPE,
  StageConfiguration,
  StageFlowNode,
  Workflow,
  WORKFLOW_FULL_SHAPE,
  WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE,
  WorkflowTemplateVersion,
} from './WorkflowConstants';

interface WorkflowConfigureScreenProps {
  workflow: Workflow;
  workflowTemplateVersion: WorkflowTemplateVersion;
  stageConfigurations: Array<StageConfiguration>;
  stageConfigurationIdToStageFlowNodes: {[stageConfigurationId: string]: Array<StageFlowNode>};
}

interface State {
  isConfigureStageModalOpen: boolean;
  editingStageConfiguration: StageConfiguration | null;
  initialStageRunRows: Array<StageRunRow>;
}

class WorkflowConfigureScreen extends React.Component<WorkflowConfigureScreenProps, State> {
  static propTypes = {
    workflow: WORKFLOW_FULL_SHAPE.isRequired,
    workflowTemplateVersion: WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE.isRequired,
    stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_FULL_SHAPE.isRequired).isRequired,
    stageConfigurationIdToStageFlowNodes: PropTypes.objectOf(
      PropTypes.arrayOf(STAGE_FLOW_NODE_FULL_SHAPE.isRequired)
    ).isRequired,
  };

  override state: State = {
    isConfigureStageModalOpen: false,
    // If editingStageConfiguration is not null ConfigureStageModal is open for editing the stage
    editingStageConfiguration: null,
    initialStageRunRows: [{inputType: BenchlingConfig.ENABLE_BATCHES ? InputType.BATCH : InputType.FILE}],
  };

  override componentDidMount(): void {
    SidepanelActions.expand();
  }

  _handleOpenCreateStageConfigurationModal = (): void => {
    this.setState({isConfigureStageModalOpen: true});
  };

  _handleCloseConfigureStageModal = (): void => {
    this.setState({
      isConfigureStageModalOpen: false,
      editingStageConfiguration: null,
    });
  };

  _isConfigureWorkflowStageModalOpen(): boolean {
    return Boolean(this.state.editingStageConfiguration || this.state.isConfigureStageModalOpen);
  }

  _handleEditStageConfiguration = (stageConfiguration: StageConfiguration): void => {
    this.setState({
      editingStageConfiguration: stageConfiguration,
    });
  };

  _handleInitializeWorkflow = (): void => {
    const initialStageRunParams = [];
    for (const row of this.state.initialStageRunRows) {
      if (row.batch != null) {
        initialStageRunParams.push({
          batchId: row.batch.id,
        });
      } else if (row.file != null) {
        initialStageRunParams.push({
          fileId: row.file.id,
        });
      }
    }
    WorkflowActions.initializeWorkflow({
      workflowId: this.props.workflow.id,
      initialStageRunParams,
    });
  };

  _isInitializeWorkflowEnabled(): boolean {
    for (const stage of this.props.stageConfigurations) {
      if (stage.stageFlowNodes.length > 0) {
        return true;
      }
    }
    return false;
  }

  _handleBack = (): void => {
    WorkflowActions.openWorkflowDashboardScreen();
  };

  _handleInitializationRowsChanged = (initialStageRunRows: Array<StageRunRow>): void => {
    this.setState({initialStageRunRows});
  };

  _wasInitializedFromRequest(): boolean {
    return this.props.workflow.initializationDatas.length !== 0;
  }

  override render(): JSX.Element {
    return (
      <nyc.Container size="lg" className="workflows-configuration-container">
        <ModalAnchor open={this._isConfigureWorkflowStageModalOpen()}>
          <ConfigureStageModal
            baseWorkflowTemplateVersionId={this.props.workflow.baseWorkflowTemplateVersionId}
            stageConfiguration={this.state.editingStageConfiguration}
            workflowTemplateVersionId={this.props.workflowTemplateVersion.id}
            onRequestClose={this._handleCloseConfigureStageModal}
          />
        </ModalAnchor>
        <WorkflowConfigurationHeader
          workflow={this.props.workflow}
          stageConfigurations={this.props.stageConfigurations}
          stageConfigurationIdToStageFlowNodes={this.props.stageConfigurationIdToStageFlowNodes}
        />
        <nyc.Row className="u-padding-bottom">
          <nyc.Col lg={22}>
            <div className="workflowConfigureScreen-body">
              <div className="workflowConfigureScreen-configureStepContainer">
                <ConfigureStep stepNumber={1} title="Edit stages">
                  <div className="workflowConfigureScreen-configureStepContent">
                    <StageConfigurationTable
                      isEditable={this.props.workflow.permissions.writable}
                      onCreateStageConfiguration={this._handleOpenCreateStageConfigurationModal}
                      onEditStageConfiguration={this._handleEditStageConfiguration}
                      stageConfigurations={this.props.stageConfigurations}
                    />
                  </div>
                </ConfigureStep>
              </div>
              <div className="workflowConfigureScreen-configureStepContainer">
                {!this._wasInitializedFromRequest() && (
                  <ConfigureStep
                    stepNumber={2}
                    title={getWorkflowDisplayString(
                      'Select legacy workflow inputs',
                      'Select workflow inputs'
                    )}
                  >
                    <div className="workflowConfigureScreen-configureStepContent">
                      <Row className="u-spacing-top">
                        <Col md={4}>
                          <WorkflowInputTable
                            initialStageRunRows={this.state.initialStageRunRows}
                            initialStageRunRowsChanged={this._handleInitializationRowsChanged}
                          />
                        </Col>
                      </Row>
                    </div>
                  </ConfigureStep>
                )}
              </div>
              <div className="u-lr-split">
                <div>
                  <Button
                    key="back"
                    className="u-tiny-spacing-right"
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    disabled={false}
                    onClick={this._handleBack}
                    size={ButtonSize.MEDIUM}
                    text="Back"
                  />
                  <Button
                    key="start"
                    buttonStyle={ButtonStyle.SECONDARY}
                    disabled={!this._isInitializeWorkflowEnabled()}
                    onClick={this._handleInitializeWorkflow}
                    size={ButtonSize.MEDIUM}
                    text={getWorkflowDisplayString('Start Legacy Workflow', 'Start Workflow')}
                  />
                </div>
              </div>
            </div>
          </nyc.Col>
        </nyc.Row>
      </nyc.Container>
    );
  }
}

export default WorkflowConfigureScreen;
